import classNames from 'classnames';
import { FaArrowTrendDown, FaArrowTrendUp } from 'react-icons/fa6';
import { toNumberString } from '@client/toolkit';
import { VatVarianceDto } from '@client/api';
import { useTranslation } from 'react-i18next';

export const VatVarianceDisplay = ({
  variance,
  size = 'sm',
  showSecondary = true,
}: {
  variance?: VatVarianceDto;
  size?: 'sm' | '2xl' | '3xl';
  showSecondary?: boolean;
}) => {
  const { t } = useTranslation('reporting');

  if (!variance) {
    return <span className="text-white">-</span>;
  }

  const { absolutePp, relativePercent, status } = variance;

  if (status === 'New') {
    return (
      <span className={classNames('text-brand-primary', `text-${size}`)}>
        {t('vatPercentage.new')}
      </span>
    );
  }

  if (status === 'N/A' || status === 'NoData') {
    return (
      <span className={classNames('text-gray-400', `text-${size}`)}>
        {t('vatPercentage.notAvailable')}
      </span>
    );
  }

  if (absolutePp === null || absolutePp === undefined) {
    return <span className="text-white">-</span>;
  }

  const direction = absolutePp === 0 ? 'neutral' : absolutePp > 0 ? 'up' : 'down';
  const formattedPp = toNumberString(absolutePp);
  const ppString = direction === 'up' ? `+${formattedPp}` : `${formattedPp}`;

  const hasValidRelative = relativePercent !== null && relativePercent !== undefined && status !== 'NoPrior';
  const formattedRelative = hasValidRelative ? toNumberString(relativePercent!) : null;
  const relativeString = hasValidRelative
    ? relativePercent! > 0
      ? `+${formattedRelative}%`
      : `${formattedRelative}%`
    : null;

  const noPriorLabel = status === 'NoPrior' ? t('vatPercentage.noPrior') : null;

  return (
    <span
      className={classNames(
        'flex items-baseline gap-1',
        direction === 'up' ? 'text-positive' : direction === 'down' ? 'text-negative' : 'text-white',
        `text-${size}`,
      )}
    >
      {absolutePp !== 0 && (direction === 'up' ? <FaArrowTrendUp /> : <FaArrowTrendDown />)}
      <span>{ppString} pp</span>
      {showSecondary && (
        <span className="text-xs text-gray-400 ml-1">
          {hasValidRelative ? `(${relativeString})` : noPriorLabel ? `(${noPriorLabel})` : null}
        </span>
      )}
    </span>
  );
};
